import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { MuscleVolumeEntry } from 'services/workout-loader.service';
import { MuscleVolumePageParams, MuscleVolumePageResult } from './muscle-volume-page-interface';
import { SvPlanHandlerService, VolumeChange } from 'services/server/sv-plan-handler.service';
import { LoadingController } from 'providers/loading-controller';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { UserDb } from 'services/user-db.service';
import { ExerciseDbService } from 'services/exercise-db.service';
import { LoggingService } from 'services/logging.service';
import { DatabaseWrapper, DocWatcher } from 'services/database-wrapper';
import { buildMuscleVolumeEntries, buildMuscleVolumeInputsAsync } from 'util/muscle-volume-helpers';
import { untranslatedString } from 'models/translated-string';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { IEditExercisePage } from 'pages/edit-exercise/edit-exercise-interface';
import { isCustomExerciseById } from 'services/exercise-db.service';

// #3790: one uncounted exercise resolved to a display name + the exercise id needed to open
// its edit page (where the "Most Similar Exercise" field lives).
type UncountedExercise = { subSlotId: string; exerciseId: string; name: string };

@Component({
  selector: 'page-muscle-volume',
  templateUrl: 'muscle-volume-page.html',
  styleUrls: ['muscle-volume-page.scss'],
  host: { 'class': 'muscle-volume-page' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: ModalPageMarker, useValue: new ModalPageMarker({ width: 480, height: 'auto' }) }]
})
export class MuscleVolumePage extends CsPage<MuscleVolumePageParams, MuscleVolumePageResult> {
  protected LL = LL;
  protected lowerBodyMuscles: MuscleVolumeEntry[] = [];
  protected upperBodyMuscles: MuscleVolumeEntry[] = [];
  protected beforeVolumes: { [muscleName: string]: number } | undefined;
  protected reoptimizeData: MuscleVolumePageParams['local']['reoptimize'];
  protected hasReoptimized = false;
  protected hasChanges = false;
  // #3790: exercises the calculator could not attribute to any muscle, resolved to names.
  // Split because only the user's own exercises can be acted on — the untracked ones are
  // listed but not tappable (#4737). Why, and what the two populations are:
  // design-docs/volume-reoptimize.md, "What lands in that list".
  protected uncountedFixableExercises: UncountedExercise[] = [];
  protected uncountedUntrackedExercises: UncountedExercise[] = [];
  private appliedChanges: VolumeChange[] = [];
  /**
   * The weekly set-volume target the last re-optimize solved to, kept so handleSave can
   * store it beside the set counts it stores (#5381). Undefined until a re-optimize has
   * run in this visit, which is what stops a plain Save from writing a stale number.
   */
  private reoptimizedTrainingVolumeWeeklyAvg: number | undefined = undefined;

  private docWatcher: DocWatcher;
  // ionViewWillEnter runs on every entry, including the return from the exercise edit page we
  // push below. Guard the one-time setup so re-entry is handled by the explicit return callback.
  private initialized = false;

  constructor(
    public navCtrl: NavController,
    private cdr: ChangeDetectorRef,
    private svPlanHandler: SvPlanHandlerService,
    private loadingCtrl: LoadingController,
    private alertCtrl: AlertController,
    private userDb: UserDb,
    private exerciseDb: ExerciseDbService,
    private logging: LoggingService,
  ) {
    super();
  }

  async ionViewWillEnter() {
    // one-time setup; re-entry (return from the exercise edit page) is handled by
    // onUncountedExerciseEdited, not by re-running this.
    if (this.initialized) return;
    this.initialized = true;
    this.docWatcher = this.userDb.database.createDocWatcher(() => {});
    this.lowerBodyMuscles = this.navParams.data.local.lowerBodyMuscles;
    this.upperBodyMuscles = this.navParams.data.local.upperBodyMuscles;
    this.reoptimizeData = this.navParams.data.local.reoptimize;
    await this.resolveUncountedAsync(this.navParams.data.local.uncountedSubSlotIds ?? [], this.reoptimizeData?.fitnessPlan);
    // guard against NG0911: the modal may be closed while the awaits above are in flight
    if (this.viewDestroyed) return;
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }

  /** Open the edit page for an uncounted exercise, where the "Most Similar Exercise" field lives.
   *  Setting it gives the exercise muscle factors so it joins the volume count (#3790). */
  async openUncountedExercise(item: UncountedExercise) {
    await this.navCtrl.pushAsync(IEditExercisePage, {
      id: item.exerciseId,
      returnCallback: 'onUncountedExerciseEdited',
    });
  }

  /** Return path from the exercise edit page. The user may have set a "Most Similar Exercise",
   *  which is saved on the custom-exercise doc (not on the plan). Rebuild overrides + volumes so a
   *  now-attributable exercise drops out of the "not counted" note and its sets join the totals. */
  async onUncountedExerciseEdited() {
    // NavController invokes return callbacks fire-and-forget (not awaited), so a rejection here
    // would become an unhandled promise rejection. Surface it instead — the recompute reads the DB
    // and can fail; the panel just keeps showing the prior numbers if it does.
    try {
      await this.recomputeFromPlanAsync();
    } catch (e) {
      this.logging.trackAbnormalException(e);
    }
  }

  private async recomputeFromPlanAsync() {
    if (!this.reoptimizeData?.fitnessPlan) return;
    const { fitnessPlan, declaredState, fitnessState } = this.reoptimizeData;
    const inputs = await buildMuscleVolumeInputsAsync(fitnessPlan, declaredState, fitnessState, this.userDb, this.exerciseDb, this.docWatcher);
    const volumes = buildMuscleVolumeEntries(fitnessPlan, declaredState, this.logging, inputs);
    this.lowerBodyMuscles = volumes.lowerBodyMuscles;
    this.upperBodyMuscles = volumes.upperBodyMuscles;
    await this.resolveUncountedAsync(volumes.uncountedSubSlotIds, fitnessPlan);
    // guard against NG0911: the modal may be closed while the awaits above are in flight
    if (this.viewDestroyed) return;
    this.cdr.markForCheck();
  }

  /** Resolve uncounted subSlotIds to display names (custom-exercise name, or the catalog name as a
   *  fallback) plus the exercise id needed to open the edit page. Names come from the plan's
   *  subSlots, so a fitnessPlan is required — it always travels via `reoptimize.fitnessPlan`, which
   *  both callers (edit-workouts, program-settings) always pass. Without it (no plan to resolve
   *  against) we degrade to an empty list rather than show ids the user can't act on. */
  private async resolveUncountedAsync(subSlotIds: string[], fitnessPlan?: FitnessPlan) {
    if (!subSlotIds?.length || !fitnessPlan) {
      this.uncountedFixableExercises = [];
      this.uncountedUntrackedExercises = [];
      return;
    }
    const customExercises = await this.userDb.getAllCustomExercisesAsync();
    const fixable: UncountedExercise[] = [];
    const untracked: UncountedExercise[] = [];
    for (const subSlotId of subSlotIds) {
      const subSlot = fitnessPlan.subSlots[subSlotId];
      if (!subSlot) continue;
      const exerciseId = subSlot.exercise_id;
      const customEx = customExercises.find(e => e._id === exerciseId);
      const name = customEx
        ? customEx.name
        : await this.exerciseDb.getExerciseNameByIdAsync(exerciseId, this.docWatcher);
      // Whether the user owns the exercise is exactly whether they can do anything about
      // it: "Most Similar Exercise" is a field on the user's own exercises only, so a
      // built-in one can never be given muscle factors this way (#4737). A user-created
      // exercise whose document has since been deleted still counts as theirs and stays
      // tappable — its edit page then says the exercise is no longer available.
      (isCustomExerciseById(exerciseId) ? fixable : untracked).push({ subSlotId, exerciseId, name });
    }
    this.uncountedFixableExercises = fixable;
    this.uncountedUntrackedExercises = untracked;
  }

  async ionViewCanLeave(): Promise<{ result: MuscleVolumePageResult } | true> {
    // in draft mode, return applied changes so the parent page can apply them to its own draft
    // (local params are serialized via sessionStorage, so the modal's fitnessPlan is a separate copy)
    if (this.reoptimizeData?.mode === 'draft' && this.appliedChanges.length > 0) {
      return { result: { appliedChanges: this.appliedChanges } };
    }
    return true;
  }

  async handleReoptimizeVolume() {
    const confirmed = await this.alertCtrl.showAlertAsync<boolean>({
      message: LL.editWorkouts.alerts.reoptimizeConfirm.message(),
      buttons: [
        { text: LL.editWorkouts.alerts.reoptimizeConfirm.no(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.editWorkouts.alerts.reoptimizeConfirm.yes(), role: AlertButtonRole.Primary, result: true },
      ],
    });
    if (!confirmed) return;

    const loader = this.loadingCtrl.create({ content: LL.editWorkouts.alerts.reoptimizeLoading() });
    void loader.present();

    try {
      const { fitnessPlan, declaredState, fitnessState, immutableState } = this.reoptimizeData;
      const inputs = await buildMuscleVolumeInputsAsync(fitnessPlan, declaredState, fitnessState, this.userDb, this.exerciseDb, this.docWatcher);
      const { changes, solverResults } = await this.svPlanHandler.reoptimizeVolumeAsync(
        fitnessPlan, declaredState, fitnessState, immutableState, inputs.muscleFactorOverrides, inputs.usesMyoRepsBySubSlotId,
        inputs.muscleFactorSourceSlots,
      );
      // #5381: this solve ran against the user's STORED declared state, so its target is
      // the right one for the plan by definition. Keeping it lets handleSave repair a plan
      // still carrying the stale number an older "Update Only My Set Volumes" left behind.
      // A user whose plan is already correct simply gets the same number written back.
      this.reoptimizedTrainingVolumeWeeklyAvg = solverResults.trainingVolumeWeeklyAvg;

      void loader.dismiss();

      if (changes.length === 0) {
        await this.alertCtrl.showAlertAsync({
          title: LL.editWorkouts.alerts.reoptimizeResult.title(),
          message: LL.editWorkouts.alerts.reoptimizeResult.noChanges(),
        });
        this.hasReoptimized = true;
        // #5381: no per-exercise change does NOT mean nothing to save. A user whose set
        // counts were re-solved by an older "Update Only My Set Volumes" already has the
        // right counts and only a stale target left over, so this branch is exactly the
        // one such a user lands in. Offer the save when the stored target disagrees with
        // the one just solved, or the repair would never reach the people it is for.
        if (this.reoptimizeData?.mode === 'save'
          && fitnessPlan.trainingVolumeWeeklyAvg !== this.reoptimizedTrainingVolumeWeeklyAvg) {
          this.hasChanges = true;
        }
        // guard against NG0911: the modal may be closed while the alert above is on screen,
        // so the change detector is only marked if the view is still alive
        this.safeMarkForCheck(this.cdr);
        return;
      }

      // show per-exercise summary
      await this.showChangeSummary(changes, fitnessPlan);

      // apply changes to the fitnessPlan (in-memory for draft, will be saved for 'save' mode)
      this.appliedChanges = changes;
      this.svPlanHandler.applyVolumeChanges(fitnessPlan, changes);

      // capture before volumes and recompute after
      this.beforeVolumes = {};
      for (const muscle of [...this.lowerBodyMuscles, ...this.upperBodyMuscles]) {
        this.beforeVolumes[muscle.name] = muscle.volume;
      }

      // the applied changes rewrote the base counts and cleared every volume adjustment, so
      // the maps are rebuilt from the new plan rather than reusing the ones taken before the write
      const newInputs = await buildMuscleVolumeInputsAsync(fitnessPlan, declaredState, fitnessState, this.userDb, this.exerciseDb, this.docWatcher);
      const newVolumes = buildMuscleVolumeEntries(fitnessPlan, declaredState, this.logging, newInputs);
      this.lowerBodyMuscles = newVolumes.lowerBodyMuscles;
      this.upperBodyMuscles = newVolumes.upperBodyMuscles;
      await this.resolveUncountedAsync(newVolumes.uncountedSubSlotIds, fitnessPlan);
      // guard against NG0911: the modal may be closed while the awaits above are in flight
      if (this.viewDestroyed) return;

      this.hasReoptimized = true;
      this.hasChanges = true;
      this.cdr.markForCheck();
    } catch (e) {
      void loader.dismiss();
      console.log('Reoptimize volume failed:', e);
      await this.alertCtrl.showAlertAsync({ message: e?.message || 'Failed to reoptimize volume.' });
    }
  }

  async handleSave() {
    // save mode: persist directly to DB
    // load the singleton to ensure we're saving the same reference the DB wrapper tracks
    const plan = await this.userDb.loadPlanAsync(this.docWatcher);
    // apply changes to the singleton if it's a different reference than what we modified
    if (plan !== this.reoptimizeData.fitnessPlan) {
      this.svPlanHandler.applyVolumeChanges(plan, this.appliedChanges);
    }
    // #5381: store the solved target alongside the set counts, so the number the Training
    // Settings page shows matches the program being saved. Only after a re-optimize has
    // actually run — otherwise there is no solved number and the stored one stands.
    if (this.reoptimizedTrainingVolumeWeeklyAvg !== undefined) {
      plan.trainingVolumeWeeklyAvg = this.reoptimizedTrainingVolumeWeeklyAvg;
    }
    await this.userDb.savePlanAsync(DatabaseWrapper.getTransactionId());
    await this.userDb.clearBacklogCacheAsync();
    this.hasChanges = false;
    // guard against NG0911: the modal may be closed while the saves above are in flight,
    // so the change detector is only marked if the view is still alive
    this.safeMarkForCheck(this.cdr);
  }

  private async showChangeSummary(changes: VolumeChange[], fitnessPlan: FitnessPlan) {
    // build name lookup from loaded exercises
    const nameMap: { [subSlotId: string]: string } = {};
    const customExercises = await this.userDb.getAllCustomExercisesAsync();
    for (const c of changes) {
      const subSlot = fitnessPlan.subSlots[c.subSlotId];
      if (!subSlot) { nameMap[c.subSlotId] = c.subSlotId; continue; }
      const customEx = customExercises.find(e => e._id === subSlot.exercise_id);
      nameMap[c.subSlotId] = customEx
        ? customEx.name
        : await this.exerciseDb.getExerciseNameByIdAsync(subSlot.exercise_id, this.docWatcher);
    }

    const changeLines = changes.map(c => {
      const name = nameMap[c.subSlotId] || c.subSlotId;
      return `${name}: ${c.oldSets} → ${c.newSets} sets`;
    }).join('  \n');

    await this.alertCtrl.showAlertAsync({
      title: LL.editWorkouts.alerts.reoptimizeResult.title(),
      messageMarkdown: untranslatedString(changeLines),
    });
  }
}
